import React, { useRef, useState, useEffect } from "react";
import Papa from "papaparse";
import { saveAs } from "file-saver";
import api from "../../api/api";
import "../../css/Csv.css";

const Csv = () => {
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [responseMessage, setResponseMessage] = useState(null);
  const [errorMessages, setErrorMessages] = useState([]);

  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [officeShifts, setOfficeShifts] = useState([]);

  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedDesignation, setSelectedDesignation] = useState("");
  const [selectedOfficeShift, setSelectedOfficeShift] = useState("");

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await api.get("/get/company");
        setCompanies(res.data.data);
      } catch (error) {
        console.error("Error fetching companies:", error);
      }
    };
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      const fetchDepartments = async () => {
        try {
          const res = await api.post("/get/department", {
            company_id: selectedCompany,
          });
          setDepartments(res.data.data);
        } catch (error) {
          console.error("Error fetching departments:", error);
        }
      };

      const fetchOfficeShifts = async () => {
        try {
          const res = await api.post("/get/officeShift", {
            company_id: selectedCompany,
          });
          setOfficeShifts(res.data.data);
        } catch (error) {
          console.error("Error fetching office shifts:", error);
        }
      };

      fetchDepartments();
      fetchOfficeShifts();
    } else {
      setDepartments([]);
      setDesignations([]);
      setOfficeShifts([]);
      setSelectedDepartment("");
      setSelectedDesignation("");
      setSelectedOfficeShift("");
    }
  }, [selectedCompany]);

  useEffect(() => {
    if (selectedCompany && selectedDepartment) {
      const fetchDesignations = async () => {
        try {
          const res = await api.post("/get/designation", {
            company_id: selectedCompany,
            department_id: selectedDepartment,
          });
          setDesignations(res.data.data);
        } catch (error) {
          console.error("Error fetching designations:", error);
        }
      };
      fetchDesignations();
    } else {
      setDesignations([]);
      setSelectedDesignation("");
    }
  }, [selectedCompany, selectedDepartment]);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleLogoClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleUpload = async () => {
    if (!file) {
      alert("Please select a file first.");
      return;
    }

    if (
      !selectedCompany ||
      !selectedDepartment ||
      !selectedDesignation ||
      !selectedOfficeShift
    ) {
      alert("Please select all dropdowns before uploading.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("company_id", selectedCompany);
    formData.append("department_id", selectedDepartment);
    formData.append("designation_id", selectedDesignation);
    formData.append("office_shift_id", selectedOfficeShift);

    setUploading(true);
    setResponseMessage(null);
    setErrorMessages([]);

    try {
      const response = await api.post("/add/employee/csv", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        setResponseMessage(response.data.message);
        alert(response.data.message); // Alert on success
        // Clear the form data after successful upload
        setFile(null);
        setSelectedCompany("");
        setSelectedDepartment("");
        setSelectedDesignation("");
        setSelectedOfficeShift("");
      } else {
        setResponseMessage("There was an issue processing the file.");
        alert("There was an issue processing the file."); // Alert on failure
      }

      if (response.data.errors && response.data.errors.length > 0) {
        setErrorMessages(response.data.errors);
      }
    } catch (error) {
      console.error("Upload error:", error);
      alert("File upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadSample = () => {
    const sampleData = [
      {
        firstname: "John",
        lastname: "Doe",
        email: "john@gmail.com",
        gender: "Male",
        phone: "1234567890",
        birth_date: "1990-01-01",
        country: "USA",
        city: "New York",
        district: "Manhattan",
        state: "New York",
        joining_date: "2023-01-10",
        basic_salary: "50000",
      },
      {
        firstname: "Jane",
        lastname: "Smith",
        email: "smith@gmail.com",
        gender: "Female",
        phone: "9876543210",
        birth_date: "1992-06-15",
        country: "USA",
        city: "Los Angeles",
        district: "Hollywood",
        state: "California",
        joining_date: "2022-08-20",
        basic_salary: "55000",
      },
    ];

    try {
      const csv = Papa.unparse(sampleData);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, "sample-employees.csv");
    } catch (error) {
      console.error("Error generating sample CSV:", error);
      alert("There was an issue generating the sample CSV.");
    }
  };

  return (
    <section className="employee-section csv-section">
      <div className="inner-content">
        <div className="filter-wrapper">
          <h2>Upload Employee</h2>
        </div>
        <div className="main-csv">
          <div className="content-wrapper">
            <div>
              <h2>CSV Upload / Download</h2>

              <div className="select-data">
                <div className="filed-data">
                  <select
                    value={selectedCompany}
                    onChange={(e) => setSelectedCompany(e.target.value)}
                  >
                    <option value="">Select Company</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filed-data">
                  <select
                    value={selectedDepartment}
                    onChange={(e) => setSelectedDepartment(e.target.value)}
                    disabled={!selectedCompany}
                  >
                    <option value="">Select Department</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.department}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filed-data">
                  <select
                    value={selectedDesignation}
                    onChange={(e) => setSelectedDesignation(e.target.value)}
                    disabled={!selectedDepartment}
                  >
                    <option value="">Select Designation</option>
                    {designations.map((desig) => (
                      <option key={desig.id} value={desig.id}>
                        {desig.designation}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filed-data">
                  <select
                    value={selectedOfficeShift}
                    onChange={(e) => setSelectedOfficeShift(e.target.value)}
                    disabled={!selectedCompany}
                  >
                    <option value="">Select Office Shift</option>
                    {officeShifts.map((shift) => (
                      <option key={shift.id} value={shift.id}>
                        {shift.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="uplode-csv">
                <input
                  type="file"
                  accept=".csv"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />

                <div className="uploade-filed">
                  <img src="/images/csv.png" onClick={handleLogoClick} /> Select csv
                </div>

                {file && <p className="select-msg">Selected: {file.name}</p>}

                <div className="btn-003">
                  <button onClick={handleUpload} disabled={uploading}>
                    {uploading ? "Uploading..." : "Upload CSV"}
                  </button>
                </div>
              </div>

              <div className="btn-003" id="Download-001">
                <button onClick={handleDownloadSample}>
                  Download Sample CSV
                </button>
              </div>

              {responseMessage && (
                <div
                  style={{
                    marginTop: "20px",
                    color: "#5b8c51",
                    textAlign: "center",
                  }}
                >
                  <strong>{responseMessage}</strong>
                </div>
              )}

              {errorMessages.length > 0 && (
                <div style={{ marginTop: "20px", color: "red" }}>
                  <h4>Errors:</h4>
                  <ul>
                    {errorMessages.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Csv;
